import React, { useCallback, useLayoutEffect } from 'react';
import { useFocusEffect, useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { LoadingAnimation, useResponsive, useToggleState } from '@sg/garnish';
import { IngredientAssetVersion, type Vendor } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
  useInitCustomizationMachine,
  useRetryAttempts,
} from '@sg/shared-fe-features-customization';

import { useFeatureFlag } from '@order/LaunchDarkly';
import {
  type TelemetryCustomizationEntryPoint,
  telemetryProductFromInput,
  useTrackEventEffect,
} from '@order/Telemetry';

import { NotFoundView } from '../../../../components';
import { useBraze } from '../../../../context/Braze';
import { useSetLastInteractedMenuStore } from '../../../../context/LastInteractedStore';
import { useSnapPixel } from '../../../../integrations';
import { useCustomizationContent } from '../../hooks';
import { useCustomizationExternalActions, useCustomizeCartMutations } from './hooks';
import { CustomizationCustomizeView, CustomizationDetailsView } from './views';

// ─────────────────────────────────────────────────────────────────────────────

export const Customization = (props: Props) => {
  const { restaurantSlug, productSlug } = props;

  // ─── Missing Data ────────────────────────────────────────────────────

  if (!restaurantSlug || !productSlug) {
    return <NotFoundView />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return <CustomizationReady {...props} />;
};

const CustomizationReady = (props: Props) => {
  const {
    restaurantSlug,
    productSlug,
    addressId,
    deliveryFee,
    vendor,
    reorderLineItemId,
    lineItemId,
    sharedLineItemId,
    sharedLineItemSlug,
    shouldShowUnavailableIngredientsModal = true,
    isEditProductMode,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
  } = props;

  const navigation = useNavigation();
  const setOptions = navigation.setOptions;
  const { currentBreakpoint } = useResponsive();
  const { logCustomEvent } = useBraze();
  const { formatMessage } = useIntl();
  const { trackViewContent: trackSnapPixelViewContent } = useSnapPixel();
  const isUpsellPricingEnabled = useFeatureFlag('CELS-5026-customization-upsell-pricing-enabled');

  // ─── Content ─────────────────────────────────────────────────────────

  const { fields: customizationContent } = useCustomizationContent();

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isOutOfStockModalOpen,
    toggleOn: openOutOfStockModal,
    toggleOff: closeOutOfStockModal,
  } = useToggleState();

  // ─── Line Item Interactions ──────────────────────────────────────────

  const { modifyCart, isAddingLineItemToCart, isEditingLineItemInCart } = useCustomizeCartMutations(
    {
      addressId,
      deliveryFee,
      vendor,
      onAddLineItemToCart,
      onEditLineItemInCart,
      onRemoveLineItemFromCart,
      sharedLineItemSlug,
    },
  );

  const shouldDisableControls = isAddingLineItemToCart || isEditingLineItemInCart;

  // ─────────────────────────────────────────────────────────────────────

  const externalActions = useCustomizationExternalActions({
    handleCustomizationComplete: modifyCart,
  });

  const handleOutOfStockIngredientsFound =
    useCallback<CustomizationMachineTypes.CustomizationMachineAction>(
      (...params) => {
        externalActions.onOutOfStockIngredientsFound?.(...params);
        openOutOfStockModal();
      },
      [externalActions, openOutOfStockModal],
    );

  const actor = useInitCustomizationMachine(
    {
      productSlug,
      restaurantSlug,
      lineItemId: lineItemId ?? reorderLineItemId,
      sharedLineItemId,
      ingredientAssetVersion: IngredientAssetVersion.V2,
      isDelivery: Boolean(addressId),
      isUpsellPricingEnabled,
    },
    {
      actions: {
        ...externalActions,
        onOutOfStockIngredientsFound: handleOutOfStockIngredientsFound,
      },
      guards: {
        checkIfShouldStartCustomization: () => true,
        checkIfShouldShowOutOfStockIngredients: () => shouldShowUnavailableIngredientsModal,
      },
    },
  );

  // ─── Context ─────────────────────────────────────────────────────────

  const { isFetching, hasFailedToFetch, isCustomizationActive, product, restaurant, quantity } =
    useCustomizationContext({ actor });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const { completeCustomization, retryFetchCustomizationData } = useCustomizationHelpers({ actor });

  const { hasReachedAttemptLimit: hasReachedFetchAttemptLimit, retry: retryFetch } =
    useRetryAttempts({ onRetry: retryFetchCustomizationData });

  const trackCustomizationViewBrazeEvent = useCallback(() => {
    logCustomEvent('pdp.view');
  }, [logCustomEvent]);

  const trackCustomizationViewSnapPixelEvent = useCallback(() => {
    if (!product) return;

    trackSnapPixelViewContent({
      productId: product.id,
      productName: product.name,
    });
  }, [trackSnapPixelViewContent, product]);

  // ─── Telemetry ───────────────────────────────────────────────────────

  const customizationEntryPoint = getCustomizationEntryPoint({
    isEditProductMode,
    reorderLineItemId,
    sharedLineItemSlug,
  });

  useTrackEventEffect({
    name: 'ga.view_item',
    payload: {
      entryPoint: customizationEntryPoint,
      selectedProduct: telemetryProductFromInput({
        ...product,
        restaurantId: restaurant?.id,
        location: restaurant?.slug,
        quantity: quantity ?? 1,
        sharedLineItemSlug,
        isSharedLineItem: Boolean(sharedLineItemSlug),
      }),
    },
    skip: !product,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    setOptions({ headerShown: !currentBreakpoint.isXS });
  }, [currentBreakpoint.isXS, setOptions]);

  useSetLastInteractedMenuStore({
    restaurantId: restaurant?.id,
    restaurantName: restaurant?.name,
    restaurantSlug: restaurant?.slug,
    addressId,
    deliveryFee,
    vendor: vendor?.name,
    vendorRestaurantId: vendor?.restaurantId,
  });

  useFocusEffect(trackCustomizationViewBrazeEvent);
  useFocusEffect(trackCustomizationViewSnapPixelEvent);

  // ─────────────────────────────────────────────────────────────────────

  if (isFetching) {
    return <LoadingAnimation />;
  }

  if (hasFailedToFetch && !hasReachedFetchAttemptLimit) {
    return (
      <NotFoundView
        btnLabel={formatMessage(messages.retry)}
        onBtnPress={retryFetch}
        withoutFooter
      />
    );
  }

  if (hasFailedToFetch || !product || !restaurant) {
    return <NotFoundView withoutFooter />;
  }

  // ─── Customization ───────────────────────────────────────────────────

  if (isCustomizationActive) {
    return (
      <CustomizationCustomizeView
        actor={actor}
        customizationContent={customizationContent}
        addressId={addressId}
        isOutOfStockModalOpen={isOutOfStockModalOpen}
        shouldDisableControls={shouldDisableControls}
        closeOutOfStockModal={closeOutOfStockModal}
      />
    );
  }

  // ─── Dressing Details ────────────────────────────────────────────────

  return (
    <CustomizationDetailsView
      actor={actor}
      customizationContent={customizationContent}
      addressId={addressId}
      isOutpost={restaurant.isOutpost}
      isEditProductMode={isEditProductMode}
      shouldDisableControls={shouldDisableControls}
      isOutOfStockModalOpen={isOutOfStockModalOpen}
      closeOutOfStockModal={closeOutOfStockModal}
      completeCustomization={completeCustomization}
    />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// A menu press and a deep link look the same after mount, so both give `direct`.
// Use `pdp.view` to count menu presses.
const getCustomizationEntryPoint = (params: {
  isEditProductMode: boolean;
  reorderLineItemId?: string;
  sharedLineItemSlug?: string;
}): TelemetryCustomizationEntryPoint => {
  if (params.isEditProductMode) return 'bag';
  if (params.reorderLineItemId) return 'reorder';
  if (params.sharedLineItemSlug) return 'shared-line-item';

  return 'direct';
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  retry: {
    defaultMessage: 'Retry',
    description: 'Customization | Failed to fetch data | Retry button label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  restaurantSlug: string;
  productSlug: string;
  reorderLineItemId?: string;
  lineItemId?: string;
  sharedLineItemId?: string;
  sharedLineItemSlug?: string;
  shouldShowUnavailableIngredientsModal?: boolean;
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  isEditProductMode: boolean;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
};
